// src/contexts/ARGuideContext.tsx

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ARGuide } from '../types/arGuides';

interface ARGuideContextValue {
  activeGuide: ARGuide | null;
  currentStepIndex: number;
  isPaused: boolean;
  startGuide: (guide: ARGuide) => void;
  completeStep: () => void;
  nextStep: () => void;
  pauseGuide: () => void;
  resumeGuide: () => void;
  cancelGuide: () => void;
  markStepCompletedByRoute: (route: string) => void;
}

const ARGuideContext = createContext<ARGuideContextValue | undefined>(undefined);

export const ARGuideProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeGuide, setActiveGuide] = useState<ARGuide | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Refs para timers de completed/cancel — permite limpiarlos en unmount
  // y evitar el warning de React sobre actualizaciones de estado tras desmontaje.
  const completeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const startGuide = useCallback((guide: ARGuide) => {
    setActiveGuide({
      ...guide,
      status: 'active',
      steps: guide.steps.map(s => ({ ...s, completed: false }))
    });
    // Paso inicial: permite retomar una guía a mitad (tutorial de onboarding).
    // Invalid/ausente → 0 (comportamiento previo de guías del chat).
    setCurrentStepIndex(
      typeof guide.currentStep === 'number' &&
        guide.currentStep > 0 &&
        guide.currentStep < guide.steps.length
        ? guide.currentStep
        : 0,
    );
    setIsPaused(false);
  }, []);

  const completeStep = useCallback(() => {
    if (!activeGuide) return;

    const updatedSteps = [...activeGuide.steps];
    updatedSteps[currentStepIndex] = {
      ...updatedSteps[currentStepIndex],
      completed: true
    };

    setActiveGuide({ ...activeGuide, steps: updatedSteps });
    nextStep();
  }, [activeGuide, currentStepIndex]);

  const nextStep = useCallback(() => {
    if (!activeGuide) return;

    if (currentStepIndex < activeGuide.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      setActiveGuide({ ...activeGuide, status: 'completed' });
      completeTimerRef.current = setTimeout(() => setActiveGuide(null), 3000);
    }
  }, [activeGuide, currentStepIndex]);

  const pauseGuide = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeGuide = useCallback(() => {
    setIsPaused(false);
  }, []);

  const cancelGuide = useCallback(() => {
    if (activeGuide) {
      setActiveGuide({ ...activeGuide, status: 'cancelled' });
    }
    cancelTimerRef.current = setTimeout(() => setActiveGuide(null), 500);
  }, [activeGuide]);

  const markStepCompletedByRoute = useCallback((_route: string) => {
    // El avance de pasos es por click en el elemento señalado (ARGuideOverlay)
    // o por el botón "Ya lo hice" (GuideControls).
    if (!activeGuide || isPaused) return;
  }, [activeGuide, isPaused]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeGuide) {
        cancelGuide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeGuide, cancelGuide]);

  // Limpieza de timers en unmount: evita setState tras desmontaje
  useEffect(() => {
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
    };
  }, []);

  const value: ARGuideContextValue = {
    activeGuide,
    currentStepIndex,
    isPaused,
    startGuide,
    completeStep,
    nextStep,
    pauseGuide,
    resumeGuide,
    cancelGuide,
    markStepCompletedByRoute
  };

  return (
    <ARGuideContext.Provider value={value}>
      {children}
    </ARGuideContext.Provider>
  );
};

/** Igual que useARGuide pero devuelve undefined fuera del provider (ej. tests del Sidebar). */
export const useARGuideOptional = (): ARGuideContextValue | undefined => useContext(ARGuideContext);

export const useARGuide = (): ARGuideContextValue => {
  const context = useContext(ARGuideContext);
  if (!context) {
    throw new Error('useARGuide must be used within ARGuideProvider');
  }
  return context;
};
